import { useMemo, type CSSProperties } from 'react';
import type { StaffCitation } from '../../types/staff';
import { CITATION_VALENCES } from '../../types/staff';
import { prettyFacet, summarizeCitations, type ValenceCounts } from '../../utils/staffProfile';
import { cn } from '../../utils/cn';

interface StaffCitationMixProps {
  citations: Record<string, StaffCitation>;
}

/** Bar fills: deeper steps than the text tones so they sit well on the dark surface. */
const FILL: Record<string, string> = {
  good: 'bg-green-500',
  neutral: 'bg-glass-400',
  poor: 'bg-red-500',
};

const pct = (n: number, total: number) => (total ? Math.round((n / total) * 100) : 0);

const StackedBar = ({ counts, className, style }: { counts: ValenceCounts; className?: string; style?: CSSProperties }) => (
  <div
    className={cn('flex gap-[2px] rounded-full overflow-hidden', className)}
    style={style}
    role="img"
    aria-label={`${counts.good} good, ${counts.neutral} neutral, ${counts.poor} poor`}
  >
    {CITATION_VALENCES.map((v) =>
      counts[v] > 0 ? (
        <span
          key={v}
          className={cn('min-w-[6px] h-full first:rounded-l-full last:rounded-r-full', FILL[v])}
          style={{ flex: `${counts[v]} 0 0` }}
          title={`${counts[v]} ${v} (${pct(counts[v], counts.total)}%)`}
        />
      ) : null,
    )}
  </div>
);

/** Valence split of all observations, overall and per facet (bars scaled to the largest facet). */
export const StaffCitationMix = ({ citations }: StaffCitationMixProps) => {
  const summary = useMemo(() => summarizeCitations(citations), [citations]);
  const maxFacet = summary.byFacet[0]?.total ?? 1;

  if (summary.total === 0) {
    return <p className="text-sm text-glass-500 italic">No observations recorded in this window.</p>;
  }

  return (
    <div className="space-y-5">
      <div>
        <StackedBar counts={summary.byValence} className="h-4" />
        <div className="flex flex-wrap gap-x-5 gap-y-1.5 mt-3 text-xs text-glass-300">
          {CITATION_VALENCES.map((v) => (
            <span key={v} className="inline-flex items-center gap-1.5">
              <span className={cn('w-2.5 h-2.5 rounded-full', FILL[v])} aria-hidden="true" />
              <span className="capitalize">{v}</span>
              <b className="text-white tabular-nums">{summary.byValence[v]}</b>
              <span className="text-glass-500 tabular-nums">{pct(summary.byValence[v], summary.total)}%</span>
            </span>
          ))}
        </div>
      </div>
      <div className="space-y-2.5">
        {summary.byFacet.map((facet) => (
          <div key={facet.facet} className="grid grid-cols-[minmax(96px,128px)_1fr_auto] items-center gap-3">
            <span className="text-xs font-mono text-glass-300 truncate" title={prettyFacet(facet.facet)}>
              {prettyFacet(facet.facet)}
            </span>
            <div className="min-w-0 h-2.5 rounded-full bg-glass-100/10">
              <StackedBar counts={facet} className="h-2.5" style={{ width: `${Math.max(4, (facet.total / maxFacet) * 100)}%` }} />
            </div>
            <span className="text-xs font-mono text-white tabular-nums w-7 text-right">{facet.total}</span>
          </div>
        ))}
      </div>
    </div>
  );
};
